import React, { useEffect, useState } from 'react';
import Loader from '../loader/loader';
import Page from '../page';
import DropDownSelect from '../dropDownSelect';
import { useAppSelector } from '../../store/hooks';
import { useLazyGetKanbanQuery } from '../../store/slices/apiSlice/kanban-api-slice';
import { usePutPropertyScoutFieldsMutation } from '../../store/slices/apiSlice/scout-api-slice';
import { useUpdateProjectMutation } from '../../store/slices/apiSlice/project-api-slice';
import KanbanBoard from '../kanbanBoard/kanbanBoard';
import { selectCities } from '../../store/slices/mapSlice';
import { transformResponseToPropsByStatus } from '../../utilities/kanbanHelpers';
import { handleBackendError } from '../../utilities/errorHandling';
import { getKeyByValue } from '../../utilities/generalHelpers';
import { toastWarning } from '../../utilities/toast';
import {
    broadcastPropertyUpdate,
    PropertyBroadcastMessageTypes,
} from '../../services/broadcastChannels';

const MAX_RESULTS = 1000;

function Kanban({
    title,
    kanbanStatuses,
    kanbanItemRenderer,
    project = false,
}) {
    const [getKanbanData, getKanbanDataResults] = useLazyGetKanbanQuery();
    const [putPropertyScoutFields, putPropertyScoutFieldsResult] =
    usePutPropertyScoutFieldsMutation();
    const [updateProject, updateProjectResult] = useUpdateProjectMutation();
    const validCities = useAppSelector(selectCities);
    const [selectedCity, setSelectedCity] = useState(null);
    const [itemsByStatus, setItemsByStatus] = useState({});

    const getKanban = () => {
        const queryStatuses = kanbanStatuses.statusNames.map((status) =>
            getKeyByValue(kanbanStatuses.dictionary, status)
        );
        getKanbanData({
            maxResults: MAX_RESULTS,
            city: selectedCity,
            statuses: queryStatuses,
            project: project,
        });
    };

    useEffect(() => {
        if (kanbanStatuses?.statusNames.length) {
            getKanban();
        }
    }, [selectedCity, kanbanStatuses?.statusNames]);

    useEffect(() => {
        const { isError, status, isSuccess, data } = getKanbanDataResults;
        if (!isError && status === 'fulfilled' && isSuccess) {
            setItemsByStatus(
                transformResponseToPropsByStatus(
                    data,
                    kanbanStatuses.statusNames,
                    kanbanStatuses.dictionary,
                    project ? 'project_status' : 'status',
                    project ? 'project_id' : 'id'
                )
            );
            if (data.records.length >= MAX_RESULTS) {
                toastWarning(
                    `More than ${MAX_RESULTS} properties in Kanban. Some properties maybe not displayed.`
                );
            }
        }
    }, [getKanbanDataResults]);

    useEffect(() => {
        if (putPropertyScoutFieldsResult.isError) {
            handleBackendError('Put Scout Fields', putPropertyScoutFieldsResult);
        } else {
            broadcastPropertyUpdate(
                PropertyBroadcastMessageTypes.PropertyUpdated,
                putPropertyScoutFieldsResult?.originalArgs?.propertyId
            );
        }
    }, [putPropertyScoutFieldsResult]);

    useEffect(() => {
        if (updateProjectResult.isError) {
            handleBackendError('Update Project', updateProjectResult);
        } else {
            broadcastPropertyUpdate(
                PropertyBroadcastMessageTypes.ProjectUpdated,
                null
            );
        }
    }, [updateProjectResult]);

    function updatePropertyStatus(property, newStatus) {
        const { starred, source } = property;
        let psfDict = {
            starred: starred || false,
            status: newStatus,
            source: source,
        };
        putPropertyScoutFields({ propertyId: property.id, scoutFields: psfDict });
    }

    function updateProjectStatus(project, newStatus) {
        const { aro_id } = project;
        const projectUpdates = { Project: { status: newStatus } };
        updateProject({ aroId: aro_id, projectUpdates });
    }

    function onSelectCity(newCity) {
        setSelectedCity(newCity || null);
    }

    const findItem = (statusId, itemId) => {
        const sourceColumn = itemsByStatus[statusId];
        const item = sourceColumn.find(
            (itemDetails) => itemDetails.kanbanId === itemId
        );
        return item;
    };

    const updateItemsInKanban = (itemToMove, action) => {
        const { source, destination } = action;
        const sourceColumnId = source?.statusId;
        const destinationColumnId =
      destination?.index >= 0 ? destination?.statusId : null;

        // handle where it is really just re-ordering
        if (sourceColumnId && sourceColumnId === destinationColumnId) {
            const updatedColumn = [...itemsByStatus[sourceColumnId]];
            updatedColumn.splice(source.index, 1);
            updatedColumn.splice(destination.index, 0, itemToMove);
            setItemsByStatus({
                ...itemsByStatus,
                [source.statusId]: updatedColumn,
            });
            // handle moving "off the board" like marking Not Feasible
        } else if (!destinationColumnId) {
            const sourceColumn = [...itemsByStatus[sourceColumnId]];
            sourceColumn.splice(source.index, 1);
            setItemsByStatus({
                ...itemsByStatus,
                [source.statusId]: sourceColumn,
            });
        }
        // handle change of status within this kanban
        else {
            const sourceColumn = [...itemsByStatus[sourceColumnId]];
            const destinationColumn = [...itemsByStatus[destinationColumnId]];
            sourceColumn.splice(source.index, 1);
            destinationColumn.splice(destination.index, 0, itemToMove);
            setItemsByStatus({
                ...itemsByStatus,
                [source.statusId]: sourceColumn,
                [destination.statusId]: destinationColumn,
            });
        }
    };

    const handleChangeStatus = (itemId, oldStatus, newStatus) => {
        const item = findItem(oldStatus, Number(itemId));
        // optimistically updating displayed properties
        // updateItemsInKanban(item, action);
        if (item) {
            if (project) {
                updateProjectStatus(item, Number(newStatus));
            } else {
                updatePropertyStatus(item, Number(newStatus));
            }
        }
    };

    const handleChangeOrder = (statusId, updatedColumn) => {
        setItemsByStatus((items) => ({
            ...items,
            [statusId]: updatedColumn,
        }));
    };

    const handleNotFeasible = (property) => {
        const sourceColumn = itemsByStatus[property.status];
        const propertyIndex = sourceColumn.findIndex(
            (prop) => prop.id == property.id
        );

        if (propertyIndex >= 0) {
            const statusNotFeasible = getKeyByValue(
                kanbanStatuses.dictionary,
                'Not Feasible'
            );
            updateItemsInKanban(property, {
                source: { index: propertyIndex, statusId: String(property.status) },
                destination: { index: -1, statusId: statusNotFeasible },
            });
            updatePropertyStatus(property, Number(statusNotFeasible));
        }
    };

    const renderError = (errorInfo, dataType) => {
        console.log(`Error retrieving ${dataType}`, errorInfo);
        return <p>Error retrieving {dataType} data</p>;
    };

    return (
        <Page>
            <div className="min-vh-100 d-flex ps-0 ps-md-4 pe-2 pe-sm-0 pe-xs-0 flex-column">
                <div className="mb-2">
                    <div className="pb-2">
                        <h1>{title}</h1>
                    </div>
                    <div className="py-2 row">
                        <div className="col-lg-3 col-md-3 col-sm-12 col-xs-12 mb-2">
                            <DropDownSelect
                                options={[...validCities]}
                                selected={selectedCity}
                                onSelect={onSelectCity}
                                label="All Cities"
                            />
                        </div>
                    </div>
                </div>
                {getKanbanDataResults?.isLoading ? (
                    <Loader />
                ) : getKanbanDataResults.isError ? (
                    renderError(getKanbanDataResults, 'Kanban')
                ) : (
                    <KanbanBoard
                        itemsByStatus={itemsByStatus}
                        onChangeOrder={handleChangeOrder}
                        onChangeStatus={handleChangeStatus}
                        onNotFeasible={handleNotFeasible}
                        statusDictionary={kanbanStatuses.dictionary}
                        kanbanItemRenderer={kanbanItemRenderer}
                    />
                )}
            </div>
        </Page>
    );
}

export default Kanban;
